import { useState, useMemo } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Picture-Hanging Guide — pure math, no sensors.
// Computes nail positions for hanging N pictures of equal size along a
// wall, either evenly spaced or centered around a midpoint. Output is a
// list of nail-positions measured from a reference point (typically the
// left edge or the center of the wall).

const UNITS = [
  { id: "in", label: "in", short: "in" },
  { id: "cm", label: "cm", short: "cm" },
];

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};

const fmt = (n, unit) => {
  if (n == null) return "—";
  return unit === "in" ? n.toFixed(2) : n.toFixed(1);
};

export default function PictureHangingGuide() {
  const [unit, setUnit] = useState("in");
  const [wallWidth, setWallWidth] = useState("96"); // 8 ft
  const [pictureWidth, setPictureWidth] = useState("16");
  const [count, setCount] = useState("3");
  const [layout, setLayout] = useState("even"); // "even" | "centered"
  const [refPoint, setRefPoint] = useState("left"); // "left" | "center"
  const [showResults, setShowResults] = useState(true);

  const positions = useMemo(() => {
    const W = num(wallWidth);
    const P = num(pictureWidth);
    const N = parseInt(count, 10);
    if (W == null || P == null || !N || N < 1) return { err: "Enter wall width, picture width, and a count." };
    if (W <= 0 || P <= 0) return { err: "Widths must be positive numbers." };
    if (N * P > W) return { err: `${N} pictures of ${P}${unit} won't fit on a ${W}${unit} wall.` };

    // Distance between picture CENTERS, for the "even spacing" layout.
    // Each picture occupies width P; between N pictures there are N+1
    // gaps. Total gap space = W - N*P. Each gap = (W - N*P) / (N+1).
    // First picture center = leftGap + P/2.
    const gap = (W - N * P) / (N + 1);
    const centers = [];
    for (let i = 0; i < N; i++) {
      const fromLeft = gap + i * (gap + P) + P / 2;
      centers.push(fromLeft);
    }

    // For "centered" layout, group is centered around the wall midpoint
    // with a fixed picture-to-picture gap. Default gap = same as even
    // for now (could expose a custom gap input later).
    let centeredCenters = centers;
    if (layout === "centered") {
      const fixedGap = gap; // could be user-specified
      const groupWidth = N * P + (N - 1) * fixedGap;
      const leftEdge = (W - groupWidth) / 2;
      centeredCenters = [];
      for (let i = 0; i < N; i++) {
        centeredCenters.push(leftEdge + i * (P + fixedGap) + P / 2);
      }
    }

    const final = layout === "even" ? centers : centeredCenters;
    const displayed = refPoint === "center" ? final.map((x) => x - W / 2) : final;
    return {
      positions: displayed,
      gap,
      groupWidth: layout === "centered" ? N * P + (N - 1) * gap : W,
    };
  }, [wallWidth, pictureWidth, count, layout, refPoint, unit]);

  const inputStyle = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <ToolsShell title="Picture-Hanging Guide">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Plan nail positions for hanging pictures evenly on a wall. Pure math — no sensors needed.
      </p>

      {/* Units */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        {UNITS.map((u) => (
          <button
            key={u.id}
            onClick={() => setUnit(u.id)}
            style={{ background: unit === u.id ? TOOLS_ACCENT : "#1C1E2E", color: unit === u.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {u.label}
          </button>
        ))}
      </div>

      <label style={labelStyle}>Wall width ({unit})</label>
      <input value={wallWidth} onChange={(e) => setWallWidth(e.target.value)} inputMode="decimal" style={inputStyle} />

      <label style={labelStyle}>Picture width ({unit})</label>
      <input value={pictureWidth} onChange={(e) => setPictureWidth(e.target.value)} inputMode="decimal" style={inputStyle} />

      <label style={labelStyle}>Number of pictures</label>
      <input value={count} onChange={(e) => setCount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" style={inputStyle} />

      <label style={labelStyle}>Layout</label>
      <div style={{ display: "flex", gap: 6 }}>
        {[{ id: "even", label: "Evenly spaced (fill wall)" }, { id: "centered", label: "Centered group" }].map((l) => (
          <button
            key={l.id}
            onClick={() => setLayout(l.id)}
            style={{ background: layout === l.id ? TOOLS_ACCENT : "#1C1E2E", color: layout === l.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
          >
            {l.label}
          </button>
        ))}
      </div>

      <label style={labelStyle}>Measure positions from</label>
      <div style={{ display: "flex", gap: 6 }}>
        {[{ id: "left", label: "Left edge" }, { id: "center", label: "Wall center" }].map((r) => (
          <button
            key={r.id}
            onClick={() => setRefPoint(r.id)}
            style={{ background: refPoint === r.id ? TOOLS_ACCENT : "#1C1E2E", color: refPoint === r.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Result */}
      <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
        {positions.err ? (
          <div style={{ color: "#F8716A", fontSize: 14 }}>{positions.err}</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 10 }}>
              Nail positions ({refPoint === "center" ? "from wall center; negative = left of center" : "from left edge"}):
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", color: "#7C8099", fontSize: 12, fontWeight: 500, padding: "6px 0" }}>#</th>
                  <th style={{ textAlign: "right", color: "#7C8099", fontSize: 12, fontWeight: 500, padding: "6px 0" }}>Position ({unit})</th>
                </tr>
              </thead>
              <tbody>
                {positions.positions.map((p, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #1F2030" }}>
                    <td style={{ padding: "10px 0", color: "#C8CADF", fontSize: 15 }}>{i + 1}</td>
                    <td style={{ padding: "10px 0", color: TOOLS_ACCENT, fontSize: 16, fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(p, unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, fontSize: 12, color: "#7C8099" }}>
              Gap between pictures: {fmt(positions.gap, unit)} {unit}
              {layout === "centered" && `. Group width: ${fmt(positions.groupWidth, unit)} ${unit}`}
            </div>
          </>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
        Positions mark the center of each picture. For hangers offset from the picture's top edge, add the offset distance to your nail height. Always use a level when hanging — Level Pro can help.
      </p>
    </ToolsShell>
  );
}
